/**
 * BottomNav Component
 * Mobile bottom navigation bar for primary actions
 * Shows only on mobile devices (< lg breakpoint)
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Briefcase, FileText, FolderKanban, User } from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  matchPaths: string[];
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Deals',
    icon: FolderKanban,
    href: '/dashboard',
    matchPaths: ['/dashboard', '/project', '/projects'],
  },
  {
    label: 'Portfolio',
    icon: Briefcase,
    href: '/portfolio',
    matchPaths: ['/portfolio'],
  },
  {
    label: 'Knowledge',
    icon: FileText,
    href: '/documents',
    matchPaths: ['/documents'],
  },
  {
    label: 'Settings',
    icon: User,
    href: '/settings',
    matchPaths: ['/settings'],
  },
];

export const BottomNav: React.FC = () => {
  const location = useLocation();

  const isActive = (item: NavItem): boolean => {
    return item.matchPaths.some((path) => location.pathname.startsWith(path));
  };

  // Hide on auth pages
  if (location.pathname === '/login' || location.pathname === '/signup') {
    return null;
  }

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/95 backdrop-blur lg:hidden safe-area-inset-bottom"
      role="navigation"
      aria-label="Mobile navigation"
    >
      <div className="flex items-center justify-around h-16 px-2">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);

          return (
            <Link
              key={item.label}
              to={item.href}
              className={cn(
                'flex flex-col items-center justify-center flex-1 h-full gap-1',
                // Smoother transitions with cubic-bezier easing
                'transition-all duration-200 [transition-timing-function:cubic-bezier(0.32,0.72,0,1)]',
                'min-w-[64px] rounded-lg',
                // Enhanced focus states for keyboard navigation (WCAG 2.2)
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                'active:scale-95',
                active
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
            >
              <Icon className={cn(
                'h-6 w-6',
                // Enhanced icon transition with spring-like effect
                'transition-transform duration-200 [transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)]',
                active ? 'scale-110 nav-icon-active' : 'scale-100'
              )} />
              <span className={cn(
                'text-[10px] font-medium',
                'transition-colors duration-150'
              )}>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
